import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Users, Tag, MapPin, Settings2, ScrollText, Mail, UserPlus, Send, ShieldOff, ShieldCheck, KeyRound, Archive, Trash2, FileText } from "lucide-react";

const TABS = [
  { key: "users", label: "Utilisateurs", icon: Users },
  { key: "reports", label: "Notes de frais", icon: ScrollText },
  { key: "categories", label: "Catégories", icon: Tag },
  { key: "units", label: "Périmètres", icon: MapPin },
  { key: "settings", label: "Paramètres", icon: Settings2 },
  { key: "audit", label: "Journal d'audit", icon: ScrollText },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "templates", label: "Modèles", icon: FileText },
];

const ROLE_LABELS = {
  demandeur: "Demandeur", valideur: "Valideur", comptable: "Comptable",
  eveque: "Évêque / Arbitre", admin: "Administrateur", super_admin: "Super-administrateur",
};

export default function AdminPage() {
  const [tab, setTab] = useState("users");
  return (
    <div className="space-y-6 animate-enter" data-testid="admin-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Administration</h1>
        <p className="text-lg text-slate-600 mt-1">Utilisateurs, référentiels, paramètres et supervision.</p>
      </div>
      <div className="flex gap-2 flex-wrap" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`h-12 px-5 rounded-xl font-bold text-base flex items-center gap-2 transition-colors ${
              tab === t.key ? "bg-[#0369a1] text-white" : "bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300"
            }`} data-testid={`admin-tab-${t.key}`}>
            <t.icon className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "users" && <UsersTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "units" && <UnitsTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "emails" && <EmailsTab />}
      {tab === "templates" && <TemplatesTab />}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [refData, setRefData] = useState({ roles: [], org_units: [], approvers: [] });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const emptyForm = { email: "", first_name: "", last_name: "", phone: "", roles: ["demandeur"], org_unit_id: "", scope_ids: [], approver_ids: [], km_allowance: "", km_rate: "0.17", iban: "", bic: "" };
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [kmRates, setKmRates] = useState([0.17, 0.41]);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (roleFilter) params.role = roleFilter;
    api.get("/admin/users", { params }).then(({ data }) => setUsers(data.users)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/reference-data").then(({ data }) => setRefData(data)).catch(() => {});
    api.get("/admin/org-units").then(({ data }) => setRefData((p) => ({ ...p, org_units_full: data.org_units }))).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setKmRates(data.settings.km_rates || [0.17, 0.41])).catch(() => {});
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const visibleUsers = users.filter((u) => !statusFilter || u.status === statusFilter);

  const submitSetPassword = async () => {
    if (pw1 !== pw2) return toast.error("Les deux mots de passe ne sont pas identiques.");
    if (pw1.length < 10 || !/[a-zA-Z]/.test(pw1) || !/[0-9]/.test(pw1)) {
      return toast.error("Le mot de passe doit contenir au moins 10 caractères, avec des lettres et des chiffres.");
    }
    setSending(true);
    try {
      await api.post(`/admin/users/${pwTarget.id}/set-password`, { password: pw1 });
      toast.success(`Mot de passe de ${pwTarget.first_name} ${pwTarget.last_name} défini. Communiquez-le par un canal séparé.`);
      setPwTarget(null);
      setPw1("");
      setPw2("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.first_name} ${u.last_name} (${u.email}) ?\n\nCette action est irréversible. Si le compte possède des notes de frais, la suppression sera refusée — utilisez alors l'archivage.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleRole = (role) => {
    setForm((f) => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role] }));
  };

  const toggleApprover = (id) => {
    setForm((f) => ({ ...f, approver_ids: f.approver_ids.includes(id) ? f.approver_ids.filter((a) => a !== id) : [...f.approver_ids, id] }));
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      email: u.email, first_name: u.first_name, last_name: u.last_name, phone: u.phone || "",
      roles: u.roles || ["demandeur"], org_unit_id: u.org_unit_id || "", scope_ids: u.scope_ids || [],
      approver_ids: u.approver_ids || [], km_allowance: u.km_allowance ?? "",
      km_rate: String(u.km_rate ?? "0.17"), iban: u.iban || "", bic: u.bic || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.email || !form.first_name || !form.last_name) return toast.error("Nom, prénom et email sont obligatoires.");
    if (form.roles.length === 0) return toast.error("Sélectionnez au moins un rôle.");
    setSending(true);
    const payload = {
      ...form,
      org_unit_id: form.org_unit_id || null,
      km_allowance: form.km_allowance !== "" ? parseFloat(form.km_allowance) : null,
      km_rate: form.km_rate !== "" ? parseFloat(String(form.km_rate).replace(",", ".")) : null,
    };
    try {
      if (editId) {
        await api.patch(`/admin/users/${editId}`, payload);
        toast.success("Profil mis à jour.");
      } else {
        const { data } = await api.post("/admin/users", payload);
        toast.success(`Compte créé. Invitation : ${data.invitation_email === "sent" ? "email envoyé" : "échec d'envoi (voir journal emails)"}.`);
      }
      setOpen(false);
      setEditId(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const action = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const units = refData.org_units_full || [];

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex gap-3 flex-wrap">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un utilisateur…" className="input-lg !w-72" data-testid="user-search-input" />
        <Select value={roleFilter || "ALL"} onValueChange={(v) => setRoleFilter(v === "ALL" ? "" : v)}>
          <SelectTrigger className="input-lg !w-64" data-testid="user-role-filter"><SelectValue placeholder="Tous les rôles" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="ALL">Tous les rôles</SelectItem>
            <SelectItem value="demandeur">Demandeurs (prêtres, diacres…)</SelectItem>
            <SelectItem value="valideur">Valideurs</SelectItem>
            <SelectItem value="comptable">Comptables</SelectItem>
            <SelectItem value="eveque">Évêque / Arbitre</SelectItem>
            <SelectItem value="admin">Administrateurs</SelectItem>
            <SelectItem value="super_admin">Super-administrateurs</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter || "ALL"} onValueChange={(v) => setStatusFilter(v === "ALL" ? "" : v)}>
          <SelectTrigger className="input-lg !w-52" data-testid="user-status-filter"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="ALL">Tous les statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="disabled">Désactivés</SelectItem>
            <SelectItem value="archived">Archivés</SelectItem>
          </SelectContent>
        </Select>
        <p className="flex items-center text-slate-600 font-semibold" data-testid="users-count">{visibleUsers.length} compte(s)</p>
        <button onClick={() => { setEditId(null); setForm(emptyForm); setOpen(true); }} className="btn-primary-lg !h-14" data-testid="create-user-button">
          <UserPlus className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Créer un compte
        </button>
      </div>
      <div className="card-base !p-0 overflow-x-auto">
        <table className="w-full text-left" data-testid="users-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-4 font-semibold text-slate-700">Nom</th>
              <th className="p-4 font-semibold text-slate-700">Email</th>
              <th className="p-4 font-semibold text-slate-700">Rôles</th>
              <th className="p-4 font-semibold text-slate-700">Périmètre</th>
              <th className="p-4 font-semibold text-slate-700">Responsable</th>
              <th className="p-4 font-semibold text-slate-700">Statut</th>
              <th className="p-4 font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="p-4 font-semibold text-slate-900">{u.first_name} {u.last_name}</td>
                <td className="p-4 text-slate-700">{u.email}</td>
                <td className="p-4">
                  <div className="flex gap-1 flex-wrap">
                    {(u.roles || []).map((r) => <Badge key={r} variant="secondary" className="text-xs">{ROLE_LABELS[r] || r}</Badge>)}
                  </div>
                </td>
                <td className="p-4 text-slate-700">{u.org_unit_name}{u.scope_names?.length ? ` — valide : ${u.scope_names.join(", ")}` : ""}</td>
                <td className="p-4 text-slate-700">{u.approver_name}</td>
                <td className="p-4">
                  <div className="flex flex-col gap-1 items-start">
                    {u.status === "active" ? <Badge className="bg-green-100 text-green-800">Actif</Badge>
                      : u.status === "pending" ? <Badge className="bg-amber-100 text-amber-800">En attente</Badge>
                      : u.status === "archived" ? <Badge className="bg-slate-200 text-slate-600">Archivé</Badge>
                      : <Badge className="bg-red-100 text-red-800">Désactivé</Badge>}
                    {u.mfa?.enabled && <Badge className="bg-emerald-100 text-emerald-800" data-testid={`mfa-badge-active-${u.id}`}>MFA active</Badge>}
                    {!u.mfa?.enabled && u.mfa_required && <Badge className="bg-amber-100 text-amber-800" data-testid={`mfa-badge-required-${u.id}`}>MFA exigée</Badge>}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => openEdit(u)}
                      className="h-10 px-3 rounded-lg border-2 border-[#0369a1] text-[#0369a1] font-semibold text-sm"
                      data-testid={`edit-user-${u.id}`}>
                      Modifier
                    </button>
                    <button onClick={() => action(() => api.post(`/admin/users/${u.id}/invite`), "Invitation renvoyée")}
                      className="h-10 px-3 rounded-lg border-2 border-[#0369a1] text-[#0369a1] font-semibold text-sm flex items-center gap-1"
                      data-testid={`invite-user-${u.id}`}>
                      <Send className="h-4 w-4" aria-hidden="true" /> Invitation
                    </button>
                    <button onClick={() => {
                        if (!window.confirm(`Envoyer un lien de réinitialisation de mot de passe à ${u.email} ?`)) return;
                        action(async () => {
                          const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
                          if (data.email_status !== "sent") throw new Error(`Email non envoyé (${data.email_status}) — vérifiez le journal des emails`);
                        }, "Lien de réinitialisation envoyé par email");
                      }}
                      className="h-10 px-3 rounded-lg border-2 border-slate-500 text-slate-700 font-semibold text-sm flex items-center gap-1"
                      data-testid={`reset-password-${u.id}`}>
                      <KeyRound className="h-4 w-4" aria-hidden="true" /> Mot de passe
                    </button>
                    {u.mfa?.enabled && (
                      <button onClick={() => action(() => api.post(`/admin/users/${u.id}/reset-mfa`), "MFA désactivée — l'utilisateur devra la reconfigurer si elle reste exigée")}
                        className="h-10 px-3 rounded-lg border-2 border-red-600 text-red-700 font-semibold text-sm flex items-center gap-1"
                        data-testid={`reset-mfa-${u.id}`}>
                        <ShieldOff className="h-4 w-4" aria-hidden="true" /> Désactiver MFA
                      </button>
                    )}
                    <button onClick={() => action(() => api.post(`/admin/users/${u.id}/mfa-policy`, { required: !u.mfa_required }),
                        u.mfa_required ? "MFA redevenue facultative pour ce compte" : "MFA désormais exigée — configuration demandée à la prochaine connexion")}
                      className="h-10 px-3 rounded-lg border-2 border-amber-600 text-amber-700 font-semibold text-sm flex items-center gap-1"
                      data-testid={`mfa-policy-${u.id}`}>
                      <ShieldCheck className="h-4 w-4" aria-hidden="true" /> {u.mfa_required ? "MFA facultative" : "Forcer MFA"}
                    </button>
                    {(u.roles || []).some((r) => ["admin", "super_admin", "comptable", "eveque"].includes(r)) && (
                      <button onClick={() => action(() => api.post(`/admin/users/${u.id}/mfa-exempt`, { exempt: !u.mfa_exempt }),
                          u.mfa_exempt ? "Double authentification réactivée pour ce compte" : "Double authentification désactivée pour ce compte (exemption)")}
                        className="h-10 px-3 rounded-lg border-2 border-orange-600 text-orange-700 font-semibold text-sm flex items-center gap-1"
                        data-testid={`mfa-exempt-${u.id}`}>
                        <ShieldOff className="h-4 w-4" aria-hidden="true" /> {u.mfa_exempt ? "Réactiver 2FA" : "Exempter 2FA"}
                      </button>
                    )}
                    <button onClick={() => { setPwTarget(u); setPw1(""); setPw2(""); }}
                      className="h-10 px-3 rounded-lg border-2 border-emerald-600 text-emerald-700 font-semibold text-sm flex items-center gap-1"
                      data-testid={`set-password-${u.id}`}>
                      <KeyRound className="h-4 w-4" aria-hidden="true" /> Définir MDP
                    </button>
                    <button onClick={() => action(() => api.post(`/admin/users/${u.id}/archive`), u.status === "archived" ? "Compte restauré (actif)" : "Compte archivé")}
                      className="h-10 px-3 rounded-lg border-2 border-slate-500 text-slate-700 font-semibold text-sm flex items-center gap-1"
                      data-testid={`archive-user-${u.id}`}>
                      <Archive className="h-4 w-4" aria-hidden="true" /> {u.status === "archived" ? "Restaurer" : "Archiver"}
                    </button>
                    <button onClick={() => deleteUser(u)}
                      className="h-10 px-3 rounded-lg border-2 border-red-700 text-red-700 font-semibold text-sm flex items-center gap-1"
                      data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                    </button>
                    <button onClick={() => action(() => api.post(`/admin/users/${u.id}/toggle-active`), "Statut modifié")}
                      className="h-10 px-3 rounded-lg border-2 border-slate-400 text-slate-700 font-semibold text-sm"
                      data-testid={`toggle-user-${u.id}`}>
                      {u.status === "active" ? "Désactiver" : "Réactiver"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editId ? "Modifier le compte" : "Créer un compte"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div>
              <Label className="label-lg">Prénom *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="input-lg" data-testid="new-user-firstname" />
            </div>
            <div>
              <Label className="label-lg">Nom *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="input-lg" data-testid="new-user-lastname" />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-lg">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-lg" data-testid="new-user-email" />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-lg">Rôles *</Label>
              <div className="flex flex-wrap gap-3">
                {refData.roles.map((r) => (
                  <label key={r.code} className="flex items-center gap-2 border-2 border-slate-200 rounded-xl px-4 py-3 cursor-pointer hover:border-[#0369a1]"
                    data-testid={`role-checkbox-${r.code}`}>
                    <Checkbox checked={form.roles.includes(r.code)} onCheckedChange={() => toggleRole(r.code)} className="h-5 w-5" />
                    <span className="font-semibold text-slate-800">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-lg">Paroisse / service</Label>
              <Select value={form.org_unit_id} onValueChange={(v) => setForm({ ...form, org_unit_id: v })}>
                <SelectTrigger className="input-lg" data-testid="new-user-unit"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="label-lg">Responsable(s) hiérarchique(s) — plusieurs possibles, une seule validation suffit</Label>
              <div className="flex flex-wrap gap-2">
                {refData.approvers.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 border-2 border-slate-200 rounded-xl px-3 py-2 cursor-pointer hover:border-[#0369a1]"
                    data-testid={`approver-checkbox-${a.id}`}>
                    <Checkbox checked={form.approver_ids.includes(a.id)} onCheckedChange={() => toggleApprover(a.id)} className="h-5 w-5" />
                    <span className="font-medium text-slate-800">{a.first_name} {a.last_name}</span>
                  </label>
                ))}
              </div>
            </div>
            {form.roles.includes("valideur") && (
              <div className="sm:col-span-2">
                <Label className="label-lg">Périmètres de validation</Label>
                <div className="flex flex-wrap gap-2">
                  {units.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 border-2 border-slate-200 rounded-xl px-3 py-2 cursor-pointer"
                      data-testid={`scope-checkbox-${u.code}`}>
                      <Checkbox checked={form.scope_ids.includes(u.id)} className="h-5 w-5"
                        onCheckedChange={() => setForm((f) => ({ ...f, scope_ids: f.scope_ids.includes(u.id) ? f.scope_ids.filter((s) => s !== u.id) : [...f.scope_ids, u.id] }))} />
                      <span className="font-medium text-slate-800">{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="label-lg">Forfait km mensuel</Label>
              <Input type="number" value={form.km_allowance} onChange={(e) => setForm({ ...form, km_allowance: e.target.value })} className="input-lg" data-testid="new-user-km" />
            </div>
            <div>
              <Label className="label-lg">IBAN</Label>
              <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="input-lg" placeholder="FR76 …" data-testid="new-user-iban" />
            </div>
            <div>
              <Label className="label-lg">BIC</Label>
              <Input value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} className="input-lg" data-testid="new-user-bic" />
            </div>
            <div>
              <Label className="label-lg">Téléphone (permet la connexion)</Label>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-lg" placeholder="06 12 34 56 78" data-testid="new-user-phone" />
            </div>
            <div>
              <Label className="label-lg">Taux kilométrique (€ / km)</Label>
              <Select value={String(form.km_rate)} onValueChange={(v) => setForm({ ...form, km_rate: v })}>
                <SelectTrigger className="input-lg" data-testid="new-user-km-rate"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {kmRates.map((r) => <SelectItem key={r} value={String(r)}>{String(r).replace(".", ",")} € / km</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <button onClick={submit} disabled={sending} className="btn-primary-lg w-full mt-4" data-testid="create-user-submit">
            {sending ? "Enregistrement…" : editId ? "Enregistrer les modifications" : "Créer et envoyer l'invitation"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Définir le mot de passe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-lg text-slate-700">
              Compte : <strong>{pwTarget?.first_name} {pwTarget?.last_name}</strong> ({pwTarget?.email})
            </p>
            <p className="text-slate-600">
              Choisissez un mot de passe temporaire et communiquez-le à l'utilisateur par un canal séparé
              (téléphone, main propre…). Il ne sera jamais envoyé par email.
            </p>
            <div>
              <Label htmlFor="pw1" className="label-lg">Nouveau mot de passe *</Label>
              <Input id="pw1" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} className="input-lg"
                placeholder="Au moins 10 caractères, lettres et chiffres" data-testid="set-password-input" />
            </div>
            <div>
              <Label htmlFor="pw2" className="label-lg">Confirmez le mot de passe *</Label>
              <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="input-lg"
                placeholder="Saisissez-le à nouveau" data-testid="set-password-confirm-input" />
            </div>
            <button onClick={submitSetPassword} disabled={sending || !pw1 || !pw2} className="btn-primary-lg w-full" data-testid="set-password-submit-button">
              {sending ? "Enregistrement…" : "Définir ce mot de passe"}
            </button>
            <p className="text-sm text-slate-500">
              Autre option : le bouton « Mot de passe » de la liste envoie à l'utilisateur un email avec un lien
              pour choisir lui-même son mot de passe.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportsTab() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    api.get("/expense-reports", { params: { page, page_size: 30, ...(status ? { status } : {}) } })
      .then(({ data }) => { setReports(data.reports); setTotal(data.total); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [page, status]);
  useEffect(() => { load(); }, [load]);

  const filtered = reports.filter((r) =>
    !search || r.number.toLowerCase().includes(search.toLowerCase()) ||
    (r.requester_name || "").toLowerCase().includes(search.toLowerCase())
  );
  const pages = Math.max(1, Math.ceil(total / 30));

  const deleteReport = async (r) => {
    if (!window.confirm(`Supprimer définitivement la note ${r.number} (${r.requester_name}) ?\nToutes ses dépenses et justificatifs seront supprimés. Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/expense-reports/${r.id}`);
      toast.success(`Note ${r.number} supprimée.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="flex gap-3 flex-wrap items-center">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (numéro, nom)…" className="input-lg !w-80" data-testid="reports-search-input" />
        <Select value={status} onValueChange={(v) => { setStatus(v === "ALL" ? "" : v); setPage(1); }}>
          <SelectTrigger className="input-lg !w-72" data-testid="reports-status-filter"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="ALL">Tous les statuts</SelectItem>
            {["DRAFT", "PENDING_APPROVAL", "APPROVED", "PARTIALLY_APPROVED", "REJECTED", "BISHOP_ARBITRATION", "ARBITRATION_APPROVED", "ARBITRATION_PARTIAL", "ARBITRATION_REJECTED", "ACCOUNTED", "PAID", "CLOSED"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-slate-600 font-semibold">{total} note(s)</p>
      </div>
      <div className="card-base !p-0 overflow-x-auto">
        <table className="w-full text-left" data-testid="admin-reports-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-4 font-semibold text-slate-700">Note</th>
              <th className="p-4 font-semibold text-slate-700">Demandeur</th>
              <th className="p-4 font-semibold text-slate-700">Période</th>
              <th className="p-4 font-semibold text-slate-700 text-right">Demandé</th>
              <th className="p-4 font-semibold text-slate-700 text-right">Accepté</th>
              <th className="p-4 font-semibold text-slate-700">Statut</th>
              <th className="p-4 font-semibold text-slate-700"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-report-row-${r.id}`}>
                <td className="p-4 font-bold text-slate-900">{r.number}</td>
                <td className="p-4 text-slate-700">{r.requester_name}</td>
                <td className="p-4 text-slate-700">{r.period}</td>
                <td className="p-4 text-right font-semibold text-slate-900">{fmtEuros(r.total_requested_cents)}</td>
                <td className="p-4 text-right font-semibold text-[#15803d]">{fmtEuros(r.total_approved_cents)}</td>
                <td className="p-4"><StatusBadge status={r.status} label={r.status_label} /></td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <button onClick={() => navigate(`/notes/${r.id}`)}
                      className="h-10 px-4 rounded-lg border-2 border-[#0369a1] text-[#0369a1] font-semibold"
                      data-testid={`open-report-${r.id}`}>
                      Ouvrir
                    </button>
                    <button onClick={() => deleteReport(r)}
                      className="h-10 px-4 rounded-lg border-2 border-red-700 text-red-700 font-semibold flex items-center gap-1"
                      data-testid={`delete-report-${r.id}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="btn-secondary-lg !h-12 !text-base" data-testid="reports-prev-page">← Précédent</button>
        <span className="flex items-center font-semibold text-slate-700">Page {page} / {pages}</span>
        <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="btn-secondary-lg !h-12 !text-base" data-testid="reports-next-page">Suivant →</button>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ code: "", label: "", requires_receipt: true, is_km: false });

  const load = useCallback(() => {
    api.get("/admin/categories").then(({ data }) => setCategories(data.categories)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.code || !form.label) return toast.error("Code et libellé obligatoires.");
    try {
      await api.post("/admin/categories", form);
      toast.success("Catégorie créée");
      setForm({ code: "", label: "", requires_receipt: true, is_km: false });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patch = async (id, data) => {
    try {
      await api.patch(`/admin/categories/${id}`, data);
      toast.success("Catégorie mise à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="categories-tab">
      <div className="card-base flex flex-wrap gap-3 items-end">
        <div>
          <Label className="label-lg">Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input-lg !w-40" placeholder="FORMATION" data-testid="category-code-input" />
        </div>
        <div className="flex-1 min-w-52">
          <Label className="label-lg">Libellé</Label>
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-lg" placeholder="Ex : Frais de formation" data-testid="category-label-input" />
        </div>
        <label className="flex items-center gap-2 h-14">
          <Checkbox checked={form.requires_receipt} onCheckedChange={(v) => setForm({ ...form, requires_receipt: !!v })} className="h-5 w-5" data-testid="category-receipt-checkbox" />
          <span className="font-semibold text-slate-800">Justificatif obligatoire</span>
        </label>
        <button onClick={create} className="btn-primary-lg !h-14" data-testid="category-create-button">Ajouter</button>
      </div>
      <div className="card-base !p-0">
        <table className="w-full text-left" data-testid="categories-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-4 font-semibold text-slate-700">Code</th>
              <th className="p-4 font-semibold text-slate-700">Libellé</th>
              <th className="p-4 font-semibold text-slate-700">Justificatif</th>
              <th className="p-4 font-semibold text-slate-700">Kilométrique</th>
              <th className="p-4 font-semibold text-slate-700">Statut</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id} className="border-b border-slate-100" data-testid={`category-row-${c.id}`}>
                <td className="p-4 font-bold text-slate-900">{c.code}</td>
                <td className="p-4 text-slate-700">{c.label}</td>
                <td className="p-4">
                  <Checkbox checked={!!c.requires_receipt} onCheckedChange={(v) => patch(c.id, { requires_receipt: !!v })} className="h-5 w-5" data-testid={`category-receipt-toggle-${c.id}`} />
                </td>
                <td className="p-4 text-slate-700">{c.is_km ? "Oui" : "Non"}</td>
                <td className="p-4">
                  <button onClick={() => patch(c.id, { active: !c.active })}
                    className={`h-10 px-4 rounded-lg font-semibold ${c.active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}
                    data-testid={`category-active-toggle-${c.id}`}>
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnitsTab() {
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({ code: "", name: "" });

  const load = useCallback(() => {
    api.get("/admin/org-units").then(({ data }) => setUnits(data.org_units)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.code || !form.name) return toast.error("Code et nom obligatoires.");
    try {
      await api.post("/admin/org-units", form);
      toast.success("Périmètre créé");
      setForm({ code: "", name: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="units-tab">
      <div className="card-base flex flex-wrap gap-3 items-end">
        <div>
          <Label className="label-lg">Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input-lg !w-40" placeholder="PAM" data-testid="unit-code-input" />
        </div>
        <div className="flex-1 min-w-52">
          <Label className="label-lg">Nom</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-lg" placeholder="Ex : Paroisse de Pamiers" data-testid="unit-name-input" />
        </div>
        <button onClick={create} className="btn-primary-lg !h-14" data-testid="unit-create-button">Ajouter</button>
      </div>
      <div className="card-base !p-0">
        <table className="w-full text-left" data-testid="units-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr><th className="p-4 font-semibold text-slate-700">Code</th><th className="p-4 font-semibold text-slate-700">Nom</th><th className="p-4 font-semibold text-slate-700">Statut</th></tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`unit-row-${u.id}`}>
                <td className="p-4 font-bold text-slate-900">{u.code}</td>
                <td className="p-4 text-slate-700">{u.name}</td>
                <td className="p-4">{u.active ? <Badge className="bg-green-100 text-green-800">Actif</Badge> : <Badge className="bg-slate-100 text-slate-500">Inactif</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_email: "", use_tls: true });
  const [newKmRate, setNewKmRate] = useState("");
  const [purgeResult, setPurgeResult] = useState(null);
  const [sending, setSending] = useState(false);

  const purgeProduction = async () => {
    if (!window.confirm("PURGE PRODUCTION : supprimer TOUTES les notes de frais et les comptes de démonstration (@diocese-pamiers.fr, comptes de test) ?")) return;
    if (!window.confirm("Cette action est IRRÉVERSIBLE. Les comptes réels, périmètres, catégories et paramètres sont conservés. Confirmer définitivement ?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/maintenance/purge-production");
      setPurgeResult(data.deleted);
      toast.success("Purge terminée. Les comptes restants sont listés ci-dessous.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const load = useCallback(() => {
    api.get("/admin/settings").then(({ data }) => {
      setSettings(data.settings);
      setSmtp(data.settings.smtp || smtp);
    }).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (partial) => {
    setSending(true);
    try {
      await api.put("/admin/settings", partial);
      toast.success("Paramètres enregistrés");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const testEmail = async () => {
    try {
      const { data } = await api.post("/admin/settings/test-email");
      if (data.status === "sent") toast.success(`Email de test envoyé via ${data.mode.toUpperCase()}`);
      else toast.error(`Échec de l'envoi (${data.mode}) : ${data.error || "erreur inconnue"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!settings) return <p className="text-lg text-slate-600">Chargement…</p>;

  return (
    <div className="space-y-6" data-testid="settings-tab">
      <div className="card-base space-y-4">
        <h2 className="text-2xl font-semibold text-slate-900">Envoi des emails</h2>
        <div className="flex gap-3 flex-wrap">
          {[
            { mode: "resend", label: "Resend (géré par la plateforme)" },
            { mode: "smtp", label: "Serveur SMTP (Mailjet ou autre)" },
          ].map((opt) => (
            <button key={opt.mode} onClick={() => save({ email_mode: opt.mode })}
              className={`h-14 px-6 rounded-xl font-bold border-2 transition-colors ${
                settings.email_mode === opt.mode ? "bg-[#0369a1] text-white border-[#0369a1]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0369a1]"
              }`} data-testid={`email-mode-${opt.mode}`}>
              {opt.label}
            </button>
          ))}
        </div>
        {settings.email_mode === "smtp" && (
          <div className="grid sm:grid-cols-2 gap-4 border-2 border-slate-200 rounded-xl p-4" data-testid="smtp-config">
            <div>
              <Label className="label-lg">Serveur SMTP</Label>
              <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} className="input-lg" placeholder="in-v3.mailjet.com" data-testid="smtp-host-input" />
            </div>
            <div>
              <Label className="label-lg">Port</Label>
              <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })} className="input-lg" data-testid="smtp-port-input" />
            </div>
            <div>
              <Label className="label-lg">Identifiant (clé API)</Label>
              <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} className="input-lg" data-testid="smtp-username-input" />
            </div>
            <div>
              <Label className="label-lg">Mot de passe (clé secrète)</Label>
              <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} className="input-lg" data-testid="smtp-password-input" />
            </div>
            <div>
              <Label className="label-lg">Adresse d'expédition</Label>
              <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} className="input-lg" placeholder="no-reply@diocese-pamiers.fr" data-testid="smtp-from-input" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 h-14">
                <Checkbox checked={!!smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: !!v })} className="h-5 w-5" data-testid="smtp-tls-checkbox" />
                <span className="font-semibold text-slate-800">TLS (recommandé)</span>
              </label>
            </div>
            <button onClick={() => save({ smtp })} disabled={sending} className="btn-primary-lg sm:col-span-2" data-testid="smtp-save-button">
              {sending ? "Enregistrement…" : "Enregistrer la configuration SMTP"}
            </button>
          </div>
        )}
        <button onClick={testEmail} className="btn-secondary-lg" data-testid="test-email-button">
          <Mail className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Envoyer un email de test à mon adresse
        </button>
      </div>

      <div className="card-base space-y-4" data-testid="email-signature-card">
        <h2 className="text-2xl font-semibold text-slate-900">Signature des emails</h2>
        <p className="text-slate-600">
          Ce bloc HTML est ajouté automatiquement à la fin de <strong>tous</strong> les emails envoyés par
          l'application (coordonnées du diocèse, logo, mentions…). Laissez vide pour aucune signature.
        </p>
        <Label className="label-lg">Signature HTML (commune à tous les emails)</Label>
        <textarea value={settings.email_signature || ""} rows={5}
          onChange={(e) => setSettings({ ...settings, email_signature: e.target.value })}
          className="input-lg w-full font-mono text-sm"
          placeholder={'<p><strong>Diocèse de Pamiers</strong><br>1 rue de l\'Évêché, 09000 Foix</p>'}
          data-testid="email-signature-input" />
        <button onClick={() => save({ email_signature: settings.email_signature || "" })} disabled={sending}
          className="btn-primary-lg" data-testid="email-signature-save">
          {sending ? "Enregistrement…" : "Enregistrer la signature"}
        </button>
      </div>

      <div className="card-base space-y-4">
        <h2 className="text-2xl font-semibold text-slate-900">Règles métier</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-lg">Taille max des justificatifs (Mo)</Label>
            <Input type="number" min="1" max="50" value={settings.max_file_mb}
              onChange={(e) => setSettings({ ...settings, max_file_mb: parseInt(e.target.value) || 10 })}
              className="input-lg" data-testid="max-file-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-lg">Barèmes kilométriques disponibles (€ / km)</Label>
            <div className="flex gap-2 flex-wrap items-center">
              {(settings.km_rates || []).map((rate) => (
                <span key={rate} className="inline-flex items-center gap-2 bg-sky-50 border-2 border-[#0369a1] rounded-xl px-4 h-12 font-bold text-[#0369a1]" data-testid={`km-rate-chip-${rate}`}>
                  {String(rate).replace(".", ",")} €/km
                  <button onClick={() => save({ km_rates: settings.km_rates.filter((r) => r !== rate) })}
                    aria-label={`Supprimer le barème ${rate}`} data-testid={`km-rate-remove-${rate}`}
                    className="text-red-600 font-bold text-xl leading-none">×</button>
                </span>
              ))}
              <Input type="text" inputMode="decimal" value={newKmRate} onChange={(e) => setNewKmRate(e.target.value)}
                className="input-lg !w-36" placeholder="Ex : 0,25" data-testid="km-rate-new-input" />
              <button onClick={() => {
                  const v = parseFloat((newKmRate || "").replace(",", "."));
                  if (!v || v <= 0 || v > 5) return toast.error("Taux invalide (entre 0,01 et 5 €/km).");
                  const list = [...new Set([...(settings.km_rates || []), v])].sort((a, b) => a - b);
                  save({ km_rates: list });
                  setNewKmRate("");
                }} className="btn-secondary-lg !h-12 !text-base" data-testid="km-rate-add-button">
                Ajouter un barème
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-2">Ces barèmes sont proposés dans le profil de chaque utilisateur (onglet Utilisateurs → Modifier). Par défaut : 0,17 €/km.</p>
          </div>
          <div>
            <Label className="label-lg">Plafond repas (€)</Label>
            <Input type="number" step="1" min="1" value={settings.meal_ceiling}
              onChange={(e) => setSettings({ ...settings, meal_ceiling: parseFloat(e.target.value) || 50 })}
              className="input-lg" data-testid="meal-ceiling-input" />
          </div>
          <div>
            <Label className="label-lg">Relance valideur (jours)</Label>
            <Input type="number" min="1" max="30" value={settings.reminder_days}
              onChange={(e) => setSettings({ ...settings, reminder_days: parseInt(e.target.value) || 5 })}
              className="input-lg" data-testid="reminder-days-input" />
          </div>
        </div>
        <button onClick={() => save({ max_file_mb: settings.max_file_mb, meal_ceiling: settings.meal_ceiling, reminder_days: settings.reminder_days })}
          disabled={sending} className="btn-primary-lg" data-testid="save-rules-button">
          {sending ? "Enregistrement…" : "Enregistrer les règles"}
        </button>
      </div>

      <div className="card-base space-y-4 border-2 !border-red-300" data-testid="maintenance-card">
        <h2 className="text-2xl font-semibold text-[#b91c1c]">Maintenance — Initialisation de la production</h2>
        <p className="text-slate-600 text-lg">
          Supprime définitivement <strong>toutes les notes de frais</strong> (y compris celles des comptes valides)
          et les <strong>comptes de démonstration</strong> (adresses @diocese-pamiers.fr et comptes de test).
          Sont conservés : les comptes réels, les périmètres, les catégories, les paramètres et le journal d'audit.
        </p>
        <button onClick={purgeProduction} disabled={sending} className="btn-danger-lg" data-testid="purge-production-button">
          {sending ? "Purge en cours…" : "Purger les données de démonstration"}
        </button>
        {purgeResult && (
          <div className="bg-slate-50 border border-slate-300 rounded-xl p-4" data-testid="purge-result">
            <p className="font-bold text-slate-800 mb-2">Résultat de la purge :</p>
            <ul className="text-slate-700 space-y-1">
              <li>Comptes @diocese-pamiers.fr supprimés : <strong>{purgeResult.users_diocese_pamiers}</strong></li>
              <li>Comptes de test supprimés : <strong>{purgeResult.users_test}</strong></li>
              <li>Notes de frais supprimées : <strong>{purgeResult.expense_reports}</strong> (dépenses : {purgeResult.expenses}, justificatifs : {purgeResult.attachments})</li>
              <li>Comptes conservés : <strong>{(purgeResult.users_remaining || []).join(", ")}</strong></li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditTab() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");

  const load = useCallback(() => {
    api.get("/admin/audit-events", { params: { page, page_size: 30, ...(action ? { action } : {}) } })
      .then(({ data }) => { setEvents(data.events); setTotal(data.total); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [page, action]);
  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(total / 30));

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="flex gap-3 items-center flex-wrap">
        <Input value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} placeholder="Filtrer par action (ex : DECISION_RECORDED)…" className="input-lg !w-96" data-testid="audit-filter-input" />
        <p className="text-slate-600 font-semibold">{total} événement(s)</p>
      </div>
      <div className="card-base !p-0 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="audit-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-3 font-semibold text-slate-700">Date (UTC)</th>
              <th className="p-3 font-semibold text-slate-700">Acteur</th>
              <th className="p-3 font-semibold text-slate-700">Action</th>
              <th className="p-3 font-semibold text-slate-700">Objet</th>
              <th className="p-3 font-semibold text-slate-700">IP</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.seq} className="border-b border-slate-100" data-testid={`audit-row-${e.seq}`}>
                <td className="p-3 text-slate-600 whitespace-nowrap">{new Date(e.timestamp).toLocaleString("fr-FR")}</td>
                <td className="p-3 text-slate-800 font-medium">{e.actor_name}</td>
                <td className="p-3"><code className="bg-slate-100 px-2 py-1 rounded font-semibold text-slate-800">{e.action}</code></td>
                <td className="p-3 text-slate-600">{e.entity_type || "—"}</td>
                <td className="p-3 text-slate-600">{e.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="btn-secondary-lg !h-12 !text-base" data-testid="audit-prev-page">← Précédent</button>
        <span className="flex items-center font-semibold text-slate-700">Page {page} / {pages}</span>
        <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="btn-secondary-lg !h-12 !text-base" data-testid="audit-next-page">Suivant →</button>
      </div>
    </div>
  );
}

const EMPTY_TEMPLATE = { name: "", description: "", subject: "", title: "", body: "", cta_label: "", enabled: true };

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template en cours d'édition (ou EMPTY_TEMPLATE pour création)
  const [isNew, setIsNew] = useState(false);
  const [preview, setPreview] = useState(null); // { html, subject }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/email-templates")
      .then(({ data }) => setTemplates(data.templates))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (t) => {
    try {
      await api.put(`/admin/email-templates/${t.key}`, { enabled: !t.enabled });
      toast.success(t.enabled ? `Modèle « ${t.name} » désactivé — les emails de ce type ne seront plus envoyés.` : `Modèle « ${t.name} » réactivé.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer définitivement le modèle « ${t.name} » ?`)) return;
    try {
      await api.delete(`/admin/email-templates/${t.key}`);
      toast.success("Modèle supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async (t) => {
    try {
      const { data } = await api.post(`/admin/email-templates/${t.key}/test`);
      if (data.status === "sent") toast.success("Email de test envoyé à votre adresse (données d'exemple).");
      else toast.error(`Échec de l'envoi : ${data.error || "erreur inconnue"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = async () => {
    try {
      const { data } = await api.post("/admin/email-templates/preview", {
        subject: editing.subject, title: editing.title, body: editing.body, cta_label: editing.cta_label,
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.subject.trim() || !editing.body.trim()) {
      return toast.error("Nom, objet et corps sont obligatoires.");
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/email-templates", editing);
        toast.success("Modèle créé");
      } else {
        await api.put(`/admin/email-templates/${editing.key}`, {
          name: editing.name, description: editing.description, subject: editing.subject,
          title: editing.title, body: editing.body, cta_label: editing.cta_label, enabled: editing.enabled,
        });
        toast.success("Modèle enregistré");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-slate-600">
          Chaque modèle est lié à un événement de l'application (validation, arbitrage, paiement…) et est envoyé
          automatiquement lorsque cet événement se produit. Désactiver un modèle n'empêche pas l'action : l'email
          est simplement ignoré.
        </p>
        <button onClick={() => { setIsNew(true); setEditing({ ...EMPTY_TEMPLATE }); }} className="btn-primary-lg" data-testid="template-create-button">
          <FileText className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Nouveau modèle
        </button>
      </div>
      <div className="space-y-3">
        {templates.map((t) => (
          <div key={t.key} className="card-base !p-4 flex flex-wrap items-center gap-4" data-testid={`template-row-${t.key}`}>
            <div className="flex-1 min-w-56">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-slate-900 text-lg">{t.name}</p>
                {t.enabled
                  ? <Badge className="bg-green-100 text-green-800" data-testid={`template-status-${t.key}`}>Actif</Badge>
                  : <Badge className="bg-slate-200 text-slate-700" data-testid={`template-status-${t.key}`}>Désactivé</Badge>}
                {t.custom && <Badge className="bg-sky-100 text-sky-800">Personnalisé</Badge>}
              </div>
              <p className="text-slate-600 text-sm mt-1">{t.description || "Modèle libre (non lié à un événement automatique)."}</p>
              <p className="text-slate-500 text-sm mt-1">Objet : <em>{t.subject}</em></p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => { setIsNew(false); setEditing({ ...t }); }} className="btn-secondary-lg !h-11" data-testid={`template-edit-${t.key}`}>Modifier</button>
              <button onClick={() => sendTest(t)} className="btn-secondary-lg !h-11" data-testid={`template-test-${t.key}`}>
                <Send className="h-4 w-4" aria-hidden="true" /> Tester
              </button>
              <button onClick={() => toggleEnabled(t)} className="btn-secondary-lg !h-11" data-testid={`template-toggle-${t.key}`}>
                {t.enabled ? "Désactiver" : "Activer"}
              </button>
              {!t.system && (
                <button onClick={() => remove(t)} className="btn-secondary-lg !h-11 !text-red-700 !border-red-300" data-testid={`template-delete-${t.key}`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setPreview(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{isNew ? "Nouveau modèle d'email" : `Modifier — ${editing.name}`}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {editing.system && (
                  <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-xl p-3" data-testid="template-system-hint">
                    Ce modèle est lié à l'événement « {editing.key} » : il est envoyé automatiquement quand cet
                    événement se produit. Il ne peut pas être supprimé, seulement modifié ou désactivé.
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="label-lg">Nom du modèle *</Label>
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-lg" data-testid="template-name-input" />
                  </div>
                  <div>
                    <Label className="label-lg">Événement / processus lié</Label>
                    <Input value={editing.system ? editing.key : "(modèle libre)"} disabled className="input-lg bg-slate-50" data-testid="template-key-input" />
                  </div>
                </div>
                <div>
                  <Label className="label-lg">Description (visible par les admins)</Label>
                  <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="input-lg" data-testid="template-description-input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="label-lg">Objet de l'email *</Label>
                    <Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="input-lg" data-testid="template-subject-input" />
                  </div>
                  <div>
                    <Label className="label-lg">Titre affiché dans l'email *</Label>
                    <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="input-lg" data-testid="template-title-input" />
                  </div>
                </div>
                <div>
                  <Label className="label-lg">Corps de l'email (HTML) *</Label>
                  <textarea value={editing.body} rows={8} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    className="input-lg w-full font-mono text-sm" data-testid="template-body-input" />
                </div>
                <div>
                  <Label className="label-lg">Libellé du bouton (laisser vide = pas de bouton)</Label>
                  <Input value={editing.cta_label} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} className="input-lg" placeholder="Voir ma note" data-testid="template-cta-input" />
                </div>
                {(editing.variables || []).length > 0 && (
                  <div data-testid="template-variables">
                    <Label className="label-lg">Variables disponibles</Label>
                    <div className="flex gap-2 flex-wrap">
                      {editing.variables.map((v) => (
                        <code key={v} className="bg-slate-100 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-700">{`{{${v}}}`}</code>
                      ))}
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2">
                  <Checkbox checked={!!editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: !!v })} className="h-5 w-5" data-testid="template-enabled-checkbox" />
                  <span className="font-semibold text-slate-800">Modèle actif (l'email est envoyé)</span>
                </label>
                <div className="flex gap-3 flex-wrap">
                  <button onClick={save} disabled={saving} className="btn-primary-lg" data-testid="template-save-button">
                    {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                  <button onClick={openPreview} className="btn-secondary-lg" data-testid="template-preview-button">Aperçu</button>
                </div>
                {preview && (
                  <div className="border-2 border-slate-200 rounded-xl overflow-hidden" data-testid="template-preview">
                    <p className="bg-slate-50 border-b border-slate-200 p-3 text-sm text-slate-700">
                      Objet : <strong>{preview.subject}</strong> <span className="text-slate-500">(données d'exemple)</span>
                    </p>
                    <iframe title="Aperçu du modèle" srcDoc={preview.html} className="w-full h-96 bg-white" sandbox="" />
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmailsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/admin/email-logs", { params: { page_size: 50 } })
      .then(({ data }) => { setLogs(data.logs); setTotal(data.total); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div className="space-y-4" data-testid="emails-tab">
      <p className="text-slate-600 font-semibold">{total} email(s) journalisé(s)</p>
      <div className="card-base !p-0 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="emails-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-3 font-semibold text-slate-700">Date</th>
              <th className="p-3 font-semibold text-slate-700">Destinataire</th>
              <th className="p-3 font-semibold text-slate-700">Objet</th>
              <th className="p-3 font-semibold text-slate-700">Mode</th>
              <th className="p-3 font-semibold text-slate-700">Statut</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`email-row-${i}`}>
                <td className="p-3 text-slate-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                <td className="p-3 text-slate-800">{l.to}</td>
                <td className="p-3 text-slate-700">{l.subject}</td>
                <td className="p-3 text-slate-600 uppercase">{l.mode}</td>
                <td className="p-3">
                  {l.status === "sent"
                    ? <Badge className="bg-green-100 text-green-800"><ShieldCheck className="h-3 w-3 mr-1" aria-hidden="true" />Envoyé</Badge>
                    : <Badge className="bg-red-100 text-red-800" title={l.error || ""}>Échec</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
